import { SpecialSymbolType } from '@/containers/symbols/SpecialSymbolType'
import { SymbolType } from '@/containers/symbols/SymbolType'
import { SYMBOL_KEY_TO_ID, SYMBOL_KEY_TO_MULTIPLIER } from '@/gameConfigs/symbolMap'
import { Model } from '@/model/Model'

export class SpinResponseParser {
    constructor(private readonly _model: Model) {}

    public parse(data: any, sessionToken: string, lastSpinCoinValue: number): any {
        const currentRound = data?.current_round || {}
        const nextRound = data?.next_round || {}
        const clusterWins: any[] = Array.isArray(currentRound?.cluster_wins) ? currentRound.cluster_wins : []
        const previousFreebet = this._model?.spinData?.freebet
        const wasFreeSpinsBefore =
            Boolean(this._model?.roundState?.isFreeSpins) || Number(previousFreebet?.amount_left) > 0
        const resolvedCoinValue = this.resolveSpinCoinValue(currentRound, nextRound, lastSpinCoinValue)
        const roundWinCoin = this.resolveNumericValue([currentRound?.win_amount], 0)
        const roundWinCurrency = this.resolveNumericValue(
            [currentRound?.win_amount_currency, currentRound?.win_currency, currentRound?.win_amount],
            0
        )

        const firstClusterPreAval = clusterWins?.[0]?.pre_aval_matrixstr
        const initialGrid = this.parseMatrixToSlotGrid(firstClusterPreAval || currentRound?.matrix)
        const slotGrid: any[] = []

        slotGrid.push(initialGrid)

        clusterWins.forEach((clusterWin: any) => {
            const postMatrix = clusterWin?.post_aval_matrixstr
            if (typeof postMatrix === 'string' && postMatrix.length) {
                slotGrid.push(this.parseMatrixToSlotGrid(postMatrix))
            }
        })

        const winningCombinations: any[] = []

        if (clusterWins.length) {
            clusterWins.forEach((clusterWin: any) => {
                const combinations = this.parseClusterWinCombinations(clusterWin, resolvedCoinValue)
                winningCombinations.push(combinations)
            })

            if (winningCombinations.length < slotGrid.length) {
                winningCombinations.push([])
            }
        } else {
            winningCombinations.push([])
        }

        const bonusGamesWon: any[] = Array.isArray(currentRound?.bonus_games_won) ? currentRound.bonus_games_won : []
        const awardedFreeSpins = this.resolveAwardedFreeSpins(bonusGamesWon)
        const isNextRoundFreeSpins = this.isFreeSpinsRound(nextRound)
        const isNextRoundPromoSpins = this.isPromoSpinsRound(nextRound)

        const nextSpinsLeftRaw = Number(nextRound?.spins_left)
        const amountLeft = (isNextRoundFreeSpins || isNextRoundPromoSpins) && Number.isFinite(nextSpinsLeftRaw)
            ? Math.max(0, nextSpinsLeftRaw)
            : 0

        const totalAmountRaw = Number(nextRound?.num_spins)
        const currentRoundSpinsRaw = Number(currentRound?.num_spins)
        const previousTotalRaw = Number(previousFreebet?.total_amount_given)
        const resolvedTotalAmountGiven =
            Number.isFinite(totalAmountRaw) && totalAmountRaw > 0
                ? totalAmountRaw
                : Number.isFinite(currentRoundSpinsRaw) && currentRoundSpinsRaw > 0
                  ? currentRoundSpinsRaw
                  : Number.isFinite(previousTotalRaw) && previousTotalRaw > 0
                    ? previousTotalRaw
                    : Math.max(amountLeft + awardedFreeSpins, 0)

        const resolvedTotalFreeSpinsCoinWin = this.resolveNumericValue(
            [
                nextRound?.total_fs_win_amount,
                currentRound?.total_fs_win_amount,
                data?.promo_details?.total_fs_win_amount,
                data?.promo_details?.win_amount,
                data?.promo_details?.created_amount,
                previousFreebet?.total_coin_win,
                previousFreebet?.total_win,
            ],
            0
        )
        const resolvedTotalFreeSpinsCurrencyWin = this.resolveNumericValue(
            [
                nextRound?.total_fs_win_amount_currency,
                currentRound?.total_fs_win_amount_currency,
                nextRound?.total_fs_win_amount,
                currentRound?.total_fs_win_amount,
                data?.promo_details?.total_fs_win_amount_currency,
                data?.promo_details?.total_fs_win_amount,
                data?.promo_details?.win_amount_currency,
                data?.promo_details?.win_amount,
                data?.promo_details?.created_amount,
                previousFreebet?.total_win,
            ],
            resolvedTotalFreeSpinsCoinWin
        )

        const shouldExposeFreebetData =
            isNextRoundFreeSpins || isNextRoundPromoSpins || wasFreeSpinsBefore || awardedFreeSpins > 0
        const resolvedAmountType =
            isNextRoundFreeSpins || isNextRoundPromoSpins || awardedFreeSpins > 0
                ? String(nextRound?.amount_type ?? data?.promo_details?.amount_type ?? (isNextRoundPromoSpins ? 3 : ''))
                : ''

        const freebet = shouldExposeFreebetData
            ? {
                  amount_left: amountLeft,
                  coin_amount: Number(this._model?.fixedCoinsBet ?? 1),
                  coin_value: resolvedCoinValue,
                  logic: {
                      level: 0,
                      lines_played: 0,
                  },
                  total_amount_given: resolvedTotalAmountGiven,
                  total_coin_win: resolvedTotalFreeSpinsCoinWin,
                  total_win: resolvedTotalFreeSpinsCurrencyWin,
                  triggered_win_amount: Number(currentRound?.triggered_win_amount ?? 0),
                  triggered_win_amount_currency: Number(currentRound?.triggered_win_amount_currency ?? 0),
                  triggered_win_bonus_amount: Number(currentRound?.triggered_win_bonus_amount ?? 0),
                  triggered_win_bonus_amount_currency: Number(currentRound?.triggered_win_bonus_amount_currency ?? 0),
              }
            : null

        const freebetPositions = this.resolveFreebetPositions(currentRound, initialGrid, awardedFreeSpins > 0)

        const spinData = {
            balance: Number(data?.player?.balance ?? data?.balance ?? 0),
            base_win_currency: roundWinCurrency,
            bonus: {
                bonus_amount: 0,
                bonus_win_currency: 0,
                freebet_count: awardedFreeSpins,
                freebet_given: awardedFreeSpins,
                freebets_position: freebetPositions,
                jackpot_won: [] as any[],
                multipliers: Array.isArray(currentRound?.multipliers) ? currentRound.multipliers : [],
                scatter_count: awardedFreeSpins > 0 ? freebetPositions[0].length : 0,
                scatters_position: awardedFreeSpins > 0 ? freebetPositions[0] : [],
            },
            currency: String(data?.player?.currency ?? ''),
            amount_type: resolvedAmountType,
            promo_details: data?.promo_details || null,
            freebet,
            freeround: null as any,
            is_jackpot_win: false,
            jackpot_amount: 0,
            message: '',
            session_token: data?.session_token || sessionToken || '',
            slot_grid: slotGrid,
            status: Number(data?.status ?? 0),
            win_coin: roundWinCoin,
            win_currency: roundWinCurrency,
            winning_combinations: winningCombinations,
        }

        return spinData
    }

    private resolveSpinCoinValue(currentRound: any, nextRound: any, lastSpinCoinValue: number): number {
        return this.resolveNumericValue(
            [
                currentRound?.coin_value,
                nextRound?.coin_value,
                lastSpinCoinValue,
                this._model?.currentCoinBet,
                this._model?.initData?.game?.default_coin_value,
            ],
            0
        )
    }

    private toFiniteNumber(value: any): number | null {
        const parsed = Number(value)
        return Number.isFinite(parsed) ? parsed : null
    }

    private resolveNumericValue(candidates: any[], fallback: number = 0): number {
        for (const candidate of candidates) {
            const parsed = this.toFiniteNumber(candidate)
            if (parsed !== null) {
                return parsed
            }
        }

        return fallback
    }

    private isFreeSpinsRound(roundData: any): boolean {
        const type = String(roundData?.type || '').toLowerCase()
        return type === 'freespins' || type === 'freespin'
    }

    private isPromoSpinsRound(roundData: any): boolean {
        const type = String(roundData?.type || '').toLowerCase()
        return type === 'promospins' || type === 'promospin' || type.startsWith('promo')
    }

    private resolveAwardedFreeSpins(bonusGamesWon: any[]): number {
        if (!Array.isArray(bonusGamesWon) || !bonusGamesWon.length) {
            return 0
        }

        return bonusGamesWon.reduce((acc: number, item: any) => {
            const type = String(item?.type || '').toLowerCase()
            if (type !== 'freespins' && type !== 'freespin') {
                return acc
            }

            const value = Number(item?.num_spins ?? item?.spins ?? item?.amount ?? 0)
            return acc + (Number.isFinite(value) ? Math.max(0, value) : 0)
        }, 0)
    }

    private resolveFreebetPositions(currentRound: any, initialGrid: any[], detectFromGrid: boolean): number[][][] {
        const rows = Number(initialGrid?.[0]?.length || 0)
        const reels = Number(initialGrid?.length || 0)

        if (!rows || !reels) {
            return [[]]
        }

        const positions: number[][] = []

        if (detectFromGrid) {
            for (let reelIndex = 0; reelIndex < reels; reelIndex++) {
                const reel = initialGrid[reelIndex]
                if (!Array.isArray(reel)) continue

                for (let symbolIndex = 0; symbolIndex < reel.length; symbolIndex++) {
                    const symbol = reel[symbolIndex]
                    if (symbol?.id === SpecialSymbolType.SCATTER) {
                        positions.push([reelIndex, symbolIndex])
                    }
                }
            }
        }

        if (!positions.length) {
            const explicitPositions = currentRound?.bonus_games_won?.[0]?.symbol_positions
            if (Array.isArray(explicitPositions)) {
                explicitPositions.forEach((flatIndex: number) => {
                    positions.push(this.flatIndexToPosition(Number(flatIndex)))
                })
            }
        }

        return [positions]
    }

    private parseClusterWinCombinations(clusterWin: any, coinValue: number): any[] {
        const details = clusterWin?.paylinewins?.details || clusterWin?.payline_wins?.details || {}
        const clusterData = Array.isArray(details?.cluster)
            ? details.cluster
            : details?.cluster && typeof details.cluster === 'object'
              ? Object.values(details.cluster)
              : []

        const parsedCombinations = clusterData.map((item: any) => {
            const flatPositions: number[] = Array.isArray(item?.symbol_positions) ? item.symbol_positions : []
            const symbolsPositions: number[][] = flatPositions.map((flatIndex: number) =>
                this.flatIndexToPosition(flatIndex)
            )
            const symbol = String(item?.symbol || '').toLowerCase()
            const rawWinAmount = this.resolveNumericValue([item?.win_amount], 0)
            const explicitCoinWin = this.toFiniteNumber(item?.win_coin_amount)
            const explicitCurrencyWin =
                this.toFiniteNumber(item?.win_amount_currency) ??
                this.toFiniteNumber(item?.win_currency) ??
                this.toFiniteNumber(item?.base_win_currency) ??
                this.toFiniteNumber(item?.currency_win)

            const winCurrency = explicitCurrencyWin ?? rawWinAmount
            const winCoin = explicitCoinWin ?? rawWinAmount

            return {
                pay_multiplier: 0,
                symbol_count: Number(item?.num_symbols ?? symbolsPositions.length ?? 0),
                symbol_id: this.symbolKeyToId(symbol),
                symbols_positions: symbolsPositions,
                win_coin: winCoin,
                base_win_currency: winCurrency,
                win_currency: winCurrency,
            }
        })

        if (parsedCombinations.length) {
            return parsedCombinations
        }

        const fallbackWinAmount = this.resolveNumericValue(
            [
                clusterWin?.win_amount_currency,
                clusterWin?.win_currency,
                clusterWin?.base_win_currency,
                clusterWin?.win_amount,
            ],
            0
        )

        if (fallbackWinAmount <= 0) {
            return []
        }

        const fallbackFlatPositions = Array.isArray(clusterWin?.prev_won_pos)
            ? clusterWin.prev_won_pos
            : Array.isArray(clusterWin?.busted_index)
              ? clusterWin.busted_index
              : []
        const fallbackPositions = fallbackFlatPositions.map((flatIndex: number) => this.flatIndexToPosition(flatIndex))

        return [
            {
                pay_multiplier: 0,
                symbol_count: fallbackPositions.length,
                symbol_id: String(SymbolType.LP_1),
                symbols_positions: fallbackPositions,
                win_coin: fallbackWinAmount,
                base_win_currency: fallbackWinAmount,
                win_currency: fallbackWinAmount,
            },
        ]
    }

    private flatIndexToPosition(flatIndex: number): number[] {
        const safeIndex = Number(flatIndex)
        if (!Number.isFinite(safeIndex) || safeIndex < 0) {
            return [0, 0]
        }

        const columns = Number(this._model?.numColumns || 6)
        const safeColumns = Number.isFinite(columns) && columns > 0 ? columns : 6

        const reelIndex = safeIndex % safeColumns
        const symbolIndex = Math.floor(safeIndex / safeColumns)
        return [reelIndex, symbolIndex]
    }

    private parseMatrixToSlotGrid(matrix: string): any[] {
        if (typeof matrix !== 'string' || !matrix.length) {
            return []
        }

        const rows = matrix
            .split(';')
            .map((row) => row.trim())
            .filter((row) => row.length > 0)

        if (!rows.length) {
            return []
        }

        const rowCount = rows.length
        const colCount = rows[0].length
        const reels: any[] = []

        for (let col = 0; col < colCount; col++) {
            reels[col] = []
            for (let row = 0; row < rowCount; row++) {
                const symbolKey = rows[row][col]
                const multiplier = this.symbolKeyToMultiplier(symbolKey)
                reels[col].push({
                    id: this.symbolKeyToId(symbolKey),
                    ...(multiplier ? { multiplier } : {}),
                })
            }
        }

        return reels
    }

    private symbolKeyToId(symbolKey: string): number {
        const normalized = String(symbolKey || '').toLowerCase()
        return SYMBOL_KEY_TO_ID[normalized] ?? SymbolType.LP_1
    }

    private symbolKeyToMultiplier(symbolKey: string): number | null {
        const normalized = String(symbolKey || '').toLowerCase()
        return SYMBOL_KEY_TO_MULTIPLIER[normalized] ?? null
    }
}
